import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "../config/api";
import { Plus, Pencil, Trash2, Tv, X } from "lucide-react";
import BottomTabBar from "../components/BottomTabBar";
import StoreSwitcher from "../components/StoreSwitcher";
import { auth } from "../config/config-firebase";
import useMerchantStores, {
    isDefaultStoreRow,
    ACTIVE_CUSTOMER_ID_CHANGED_EVENT,
} from "../hooks/useMerchantStores";
import {
    isAdminMerchantMode,
    getActiveMerchantCustomerId,
    isPromoLoginMode,
} from "../utils/adminMerchantMode";
import { useAppNotify } from '../utils/errorHandler';


export default function ModesPage() {
    const [modes, setModes] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [modeNameInput, setModeNameInput] = useState("");
    const [editingMode, setEditingMode] = useState(null); // null = add, else edit
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [uid, setUid] = useState(null);

    const navigate = useNavigate();
    
    const { showNotify } = useAppNotify();


    useEffect(() => {
        // Check if user logged in, if not, go back to home screen

        if (!localStorage.getItem("customer_id")) {
            navigate("/");
        }
    }, []);

    // Modes are scoped by the active store's own customer_id (see
    // getMerchantStoreDetails — each store row carries its own customer_id).
    // Resolve uid the same way StoreListPage/StoreSwitcher do, then read the
    // default store's customer_id off the shared hook instead of trusting a
    // possibly-stale localStorage "customer_id".
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setUid(currentUser?.uid || localStorage.getItem("customer_id") || null);
        });
        return () => unsubscribe();
    }, []);

    // Admin Merchant Mode: the admin already picked a specific merchant in
    // LoginAsMerchant, so use that merchant's customer_id directly instead
    // of resolving stores through the Firebase-uid-based hook below (which
    // never applies to an admin session).
    //
    // Promo Login Mode: same situation — a promo device links via
    // customerId + phone (PromoLogin.js) and never signs into Firebase, so
    // there's no real uid for the hook to resolve either.
    const adminMerchantMode = isAdminMerchantMode();
    const promoLoginMode = isPromoLoginMode();
    const bypassStoreHook = adminMerchantMode || promoLoginMode;
    const { stores, loadingStores, fetchStores } = useMerchantStores(bypassStoreHook ? null : uid);
    const activeStore = stores.find(isDefaultStoreRow) || stores[0] || null;

    // Promo Login Mode has no `stores`/`activeStore` of its own (the hook is
    // bypassed above), so its customerId has to come from localStorage
    // instead — but reading it inline on every render isn't reactive: it
    // won't pick up a switch made via StoreSwitcher below (a sibling
    // component, same page) until something re-renders this component. Track
    // it as state instead, seeded from localStorage and kept in sync by the
    // ACTIVE_CUSTOMER_ID_CHANGED_EVENT listener underneath.
    const [promoCustomerId, setPromoCustomerId] = useState(() => localStorage.getItem("customer_id"));
    const customerId = adminMerchantMode
        ? getActiveMerchantCustomerId()
        : promoLoginMode
        ? promoCustomerId
        : (activeStore?.customer_id || null);
    const storesLoading = bypassStoreHook ? false : loadingStores;

    // StoreSwitcher (rendered below) uses its OWN useMerchantStores instance,
    // so switching the default store there doesn't touch this page's copy of
    // `stores` — without this, the newly-active store's data only shows up
    // after a full page reload. Refetch this instance's stores whenever a
    // switch happens anywhere on the page. In Promo Login Mode `fetchStores`
    // is a no-op (the hook was never given a uid to work with), so update
    // `promoCustomerId` directly instead — that's what actually drives
    // `customerId` for this mode.
    useEffect(() => {
        if (adminMerchantMode) return;
        const handleActiveStoreChanged = (event) => {
            if (promoLoginMode) {
                setPromoCustomerId(event?.detail?.customerId || localStorage.getItem("customer_id"));
                return;
            }
            fetchStores();
        };
        window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleActiveStoreChanged);
        return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleActiveStoreChanged);
    }, [adminMerchantMode, promoLoginMode, fetchStores]);

    const fetchModes = async (id) => {
        if (!id) return; // Exit if no customerId available yet
        try {
            const { data } = await axios.get(`${API_BASE}/getModes`, {
                params: { customer_id: id } // Query param
            });
            setModes(data?.data || []);
        } catch (err) {
            console.error(err);
            showNotify("Couldn't load your modes. Please try again.", "error");
        }
    };

    // Fetches on mount once the active store resolves, and re-fetches in
    // place whenever it changes (e.g. via the StoreSwitcher above).
    useEffect(() => {
        if (storesLoading || !customerId) return;
        fetchModes(customerId);
    }, [customerId, storesLoading]);

    const handleAddEditMode = async (values) => {
        try {
            if (editingMode) {
                // Edit existing
                await axios.post(`${API_BASE}/updateMode`, { _id: editingMode._id, ...values });
                showNotify("Mode updated!", "success");
            } else {
                if (!customerId) {
                    showNotify("Still loading your store — try again in a moment.", "info");
                    return;
                }
                // Add new
                await axios.post(`${API_BASE}/addMode`, {
                    ...values,
                    customerId,   // attach the active store's customer_id
                });
                showNotify("Mode added!", "success");
            }
            setModalVisible(false);
            setModeNameInput("");
            setEditingMode(null);
            fetchModes(customerId);
        } catch (err) {
            console.error(err);
            showNotify(editingMode ? "Couldn't save your changes. Please try again." : "Couldn't add the mode. Please try again.", "error");
        }
    };

    const handleEdit = (mode) => {
        setEditingMode(mode);
        setModeNameInput(mode.modeName);
        setModalVisible(true);
    };

    const handleDelete = async (_id) => {
        try {
            await axios.post(`${API_BASE}/deleteMode`, { _id });
            showNotify("Mode deleted!", "success");
            fetchModes(customerId);
        } catch (err) {
            console.error(err);
            showNotify(err?.response?.data?.message || err?.message || "Couldn't delete the mode. Please try again.", "error");
        }
    };

    const openAddModal = () => {
        setEditingMode(null);
        setModeNameInput("Mode 1");
        setModalVisible(true);
    };

    const submitMode = () => {
        if (!String(modeNameInput || "").trim()) {
            showNotify("Please enter mode name", "error");
            return;
        }
        handleAddEditMode({ modeName: modeNameInput });
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col w-full min-w-0">
            <StoreSwitcher />
            <div className="px-3 sm:px-4 pt-5 sm:pt-6 flex-1 pb-28 max-w-2xl w-full mx-auto min-w-0">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight">
                    Screens Manager
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Manage and organise your display modes.
                </p>

                <button
                    type="button"
                    onClick={openAddModal}
                    className="hidden sm:flex mt-4 items-center gap-1.5 bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 rounded-full"
                >
                    <Plus size={15} /> Add mode
                </button>

                <div className="flex items-center justify-between mb-3 mt-4">
                        <div className="flex items-center gap-2">
                            <span className="text-base font-bold text-gray-900">
                                Modes
                            </span>
                            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                {modes.length}
                            </span>
                        </div>
                    </div>

                    {modes.length === 0 ? (
                        <div className="mt-20 flex flex-col items-center text-gray-400">
                            <Tv size={36} className="mb-3 opacity-30" />
                            <p className="text-sm">
                                No modes yet. Tap Add mode to start.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            {modes.map((mode, index) => (
                                <div
                                    key={mode._id}
                                    className={`flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-3 sm:py-3.5 ${
                                        index !== modes.length - 1
                                            ? "border-b border-gray-100"
                                            : ""
                                    }`}
                                >
                                    <div className="min-w-0 flex-1 w-full">
                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                            {mode.modeName}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-0.5">
                                            Screen mode
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-1.5 flex-shrink-0 self-end sm:self-auto">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate(`/promo-ads/${mode._id}`)
                                            }
                                            className="bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-2 sm:py-1.5 rounded-lg min-h-[36px] sm:min-h-0"
                                        >
                                            Update
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleEdit(mode)}
                                            className="border border-gray-200 p-2 sm:p-1.5 rounded-lg min-h-[36px] min-w-[36px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                                            aria-label="Rename mode"
                                        >
                                            <Pencil size={14} className="text-gray-400" />
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setDeleteConfirm(mode)}
                                            className="bg-red-50 p-2 sm:p-1.5 rounded-lg min-h-[36px] min-w-[36px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                                            aria-label="Delete mode"
                                        >
                                            <Trash2 size={14} className="text-red-400" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>

            <BottomTabBar />

            <button
                type="button"
                onClick={openAddModal}
                className="fixed bottom-24 right-4 bg-blue-600 w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-lg z-30 sm:hidden"
                aria-label="Add mode"
            >
                <Plus size={22} className="text-white" />
            </button>

            {modalVisible && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5 sm:p-8"
                    onClick={() => setModalVisible(false)}
                >
                    <div
                        className="bg-white w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-2xl px-4 sm:px-5 py-4 sm:py-5 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-5">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingMode ? "Rename mode" : "Add mode"}
                            </h2>
                            <button type="button" onClick={() => setModalVisible(false)}>
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <div>
                            <label className="text-sm text-gray-600 font-medium mb-1 block">
                                Mode name
                            </label>
                            <input
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                placeholder="e.g. Breakfast, Lunch, Happy Hour"
                                value={modeNameInput}
                                onChange={(e) => setModeNameInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") submitMode();
                                }}
                                autoFocus
                            />
                        </div>

                        <button
                            type="button"
                            onClick={submitMode}
                            className="w-full bg-blue-600 text-white font-bold text-base py-4 rounded-full mt-6"
                        >
                            {editingMode ? "Update mode" : "Save mode"}
                        </button>
                    </div>
                </div>
            )}

            {deleteConfirm && (
                <div
                    className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-5 sm:p-8"
                    onClick={() => setDeleteConfirm(null)}
                >
                    <div
                        className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-sm shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-red-50 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <Trash2 size={22} className="text-red-500" />
                        </div>

                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            Delete mode
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Are you sure you want to delete{" "}
                            <strong className="text-gray-900">
                                "{deleteConfirm.modeName}"
                            </strong>
                            ? This cannot be undone.
                        </p>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    handleDelete(deleteConfirm._id);
                                    setDeleteConfirm(null);
                                }}
                                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-full text-sm"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
